import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderService } from '../../../services/Order/orderService';
import { AuctionDto } from '../../../services/Auction/auctionService.dto';
import { OrderDto } from '../../../services/Order/orderService.dto';
import { UserDetailsDto } from '../../../services/User/userService.dto';
import { findWinningBid, isAuctionInProgress, isAuctionWaitingForEnd, isWinnerDetermined } from '../utils/auctionUtils';
import { useError } from '../../../providers/useError';
import { useAuctionEndDetection } from './useAuctionEndDetection';
import { TechnicalUserStatus } from './useTechnicalUser';

export type OnSiteOrderStatus =
  | 'INITIALIZING'
  | 'AUCTION_IN_PROGRESS'
  | 'WAITING_FOR_RESOLUTION'
  | 'WINNER_IS_NOT_TECHNICAL'
  | 'AUCTION_HAS_NO_WINNER'
  | 'UNKNOWN_ERROR'
  | 'ORDER_NOT_ACTIVE'
  | 'ORDER_READY';

export const useOnSiteOrder = (
  technicalUserStatus: TechnicalUserStatus,
  auction?: AuctionDto,
  technicalUser?: UserDetailsDto
) => {
  const [order, setOrder] = useState<OrderDto>();
  const [orderStatus, setOrderStatus] = useState<OnSiteOrderStatus>('INITIALIZING');

  const { t } = useTranslation('onsite');
  const { errorMessage } = useError();

  const refreshStatus = async () => {
    const { status, order } = await fetchOrder();
    setOrderStatus(status);
    setOrder(order);
  };

  useAuctionEndDetection(auction, refreshStatus);

  useEffect(() => {
    refreshStatus();
  }, [auction, technicalUserStatus]);

  const fetchOrder = async (): Promise<{ status: OnSiteOrderStatus; order?: OrderDto }> => {
    if (!auction) {
      return { status: 'INITIALIZING' };
    }

    if (isAuctionInProgress(auction)) {
      return { status: 'AUCTION_IN_PROGRESS' };
    }

    if (isAuctionWaitingForEnd(auction)) {
      return { status: 'WAITING_FOR_RESOLUTION' };
    }

    if (!isWinnerDetermined(auction)) {
      return { status: 'AUCTION_HAS_NO_WINNER' };
    }

    if (!technicalUser && technicalUserStatus !== 'NOT_CREATED' && technicalUserStatus !== 'NOT_FOUND') {
      return { status: 'INITIALIZING' };
    }

    if (findWinningBid(auction).userId !== technicalUser?.id && technicalUserStatus !== 'NOT_FOUND') {
      return { status: 'WINNER_IS_NOT_TECHNICAL' };
    }

    const lastOrder = await fetchLastOrder();
    if (!lastOrder) {
      return { status: 'UNKNOWN_ERROR' };
    }

    if (!technicalUser || lastOrder.status !== 'CONFIRMED') {
      return { status: 'ORDER_NOT_ACTIVE', order: lastOrder };
    } else {
      return { status: 'ORDER_READY', order: lastOrder };
    }
  };

  const fetchLastOrder = async () => {
    try {
      const ordersResponse = await orderService.fetchOrders({ itemId: auction!!.itemId, page: 0, size: 10 });
      const orders = ordersResponse.data.content;
      if (orders) {
        const sortedOrders = sortOrder(orders, 'desc');
        return sortedOrders[0];
      }
    } catch (error) {
      errorMessage(t('order.cannot-fetch'));
    }
  };

  return { order, orderStatus };
};

type OrdersSorting = 'asc' | 'desc';

export const sortOrder = (orders: OrderDto[], ordersSorting: OrdersSorting): OrderDto[] =>
  orders.sort((order1, order2) =>
    ordersSorting === 'asc' ? compareOrder(order1, order2) : compareOrder(order2, order1)
  );

const compareOrder = (order1: OrderDto, order2: OrderDto) => order1.created.localeCompare(order2.created);
